import { z } from 'zod'
import { TrackEvent, TrackingEvent } from '../constants/track'
import { dbEntitySchema, listQueryZodFields } from './system'

export const trackingEventSchema = z.nativeEnum(TrackingEvent)

const trackUserAgentSchema = z.object({
  full: z.string().optional().nullable(),
  browserName: z.string().optional().nullable(),
  browserVersion: z.string().optional().nullable(),
  deviceModel: z.string().optional().nullable(),
  deviceType: z.string().optional().nullable(),
  deviceVendor: z.string().optional().nullable(),
  osName: z.string().optional().nullable(),
  osVersion: z.string().optional().nullable(),
  engineName: z.string().optional().nullable(),
  engineVersion: z.string().optional().nullable(),
  cpuArchitecture: z.string().optional().nullable(),
})

const trackPageSchema = z.object({
  pathname: z.string().optional(),
  search: z.string().optional(),
  hash: z.string().optional(),
  referrer: z.string().optional(),
  title: z.string().optional(),
  href: z.string().optional(),
  protocol: z.string().optional(),
  host: z.string().optional(),
  hostName: z.string().optional(),
  port: z.string().optional(),
})

export const trackGeoSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  city: z.string().optional().nullable(),
  region: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
})

export const trackContextSchema = z.object({
  page: trackPageSchema.optional(),
  userAgent: z.union([z.string(), trackUserAgentSchema]).optional(),
  geo: trackGeoSchema.partial().optional(),
})

export const trackUserSnapshotSchema = z.object({
  firstName: z.string().optional().nullable(),
  lastName: z.string().optional().nullable(),
  imageUrl: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
})

export const trackEntitySnapshotSchema = z.object({
  id: z.string(),
  name: z.string().optional().nullable(),
  imageUrl: z.string().optional().nullable(),
})

export const entityAnalyticsSchema = z.object({
  pageViews: z.number().default(0),
  uniquePageViews: z.number().default(0),
  itemViews: z.number().default(0),
  checkoutsStarted: z.number().default(0),
  purchases: z.number().default(0),
})

export const trackSchema = dbEntitySchema.extend({
  event: z.string(),
  displayName: z.string().optional(),
  organization: z.string().optional(),
  venue: z.string().optional(),
  product: z.string().optional(),
  admissionGroup: z.string().optional(),
  admissionGroupType: z.string().optional(),
  order: z.string().optional(),
  eventId: z.string().optional(),
  user: z.string().nullish(),
  prospect: z.string().nullish(),
  userSnapshot: trackUserSnapshotSchema.optional(),
  venueSnapshot: trackEntitySnapshotSchema.optional(),
  eventSnapshot: trackEntitySnapshotSchema.optional(),
  prospectGuid: z.string().optional(),
  sessionId: z.string().optional(),
  properties: z.record(z.string(), z.unknown()).optional(),
  ipAddress: z.string().optional(),
  context: trackContextSchema.optional(),
  geo: trackGeoSchema.optional().nullable(),
})

export const trackCreateArgsSchema = z.object({
  timestamp: z.number().optional(),
  event: z.string(),
  properties: z.record(z.string(), z.unknown()).optional(),
  data: z.record(z.string(), z.unknown()).optional(),
  userId: z.string().optional(),
  prospectId: z.string().optional(),
  userSnapshot: trackUserSnapshotSchema.optional(),
  prospectGuid: z.string().optional(),
  ipAddress: z.string().optional(),
  sessionId: z.string().optional(),
  context: trackContextSchema.optional(),
})

export const trackV1CreateArgsSchema = z.object({
  timestamp: z.number().optional(),
  event: trackingEventSchema,
  properties: z.object({
    venueId: z.string(),
    eventId: z.string().nullish(),
    inventoryId: z.string().optional(),
    admissionGroupId: z.string().optional(),
    orderId: z.string().optional(),
    pathname: z.string().optional(),
    organizationId: z.string().optional(),
  }),
  sessionId: z.string().optional(),
  context: trackContextSchema.optional(),
})

export const trackListQuerySchema = z.object({
  sessionId: z.string().optional(),
  event: z.union([z.string(), z.array(z.string())]).optional(),
  userId: z.string().optional(),
  prospectId: z.string().optional(),
  organizationId: z.string().optional(),
  venueId: z.union([z.string(), z.array(z.string())]).optional(),
  dateStart: z.union([z.string(), z.number(), z.date()]).optional(),
  dateEnd: z.union([z.string(), z.number(), z.date()]).optional(),
})

export const adminTrackingEventsListQuerySchema = z.object({
  ...listQueryZodFields,
  dateStart: z.coerce.date().optional(),
  dateEnd: z.coerce.date().optional(),
  organizationId: z.string().optional(),
  venueId: z.string().optional(),
  event: z.string().optional(),
})

export const adminTrackingEventListItemSchema = trackSchema.extend({
  geo: trackGeoSchema.nullable(),
})

export const adminTrackingEventsListResponseSchema = z.object({
  data: z.array(adminTrackingEventListItemSchema),
  count: z.number().int().nonnegative(),
})

export const trackEventPayloadSchema = z.object({
  id: z.string(),
})

export const trackInternalEventSchema = z.nativeEnum(TrackEvent)
